// /app/frontend/src/components/admin/OrderStatusManager.jsx
// Admin component for managing order fulfillment status

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Truck, CheckCircle, Clock, XCircle, Search,
  RefreshCw, Loader2, ChevronDown, ChevronRight, Mail, Phone,
  MapPin, Calendar, ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = [
  { value: 'pending', label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  { value: 'processing', label: 'Processing', icon: Package, color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'shipped', label: 'Shipped', icon: Truck, color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'cancelled', label: 'Cancelled', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-300' }
];

const OrderStatusManager = ({ token }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);
  
  // Bulk selection state
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/sales-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show orders that have been paid/converted
      const paidOrders = response.data.filter(order => 
        order.status === 'converted_to_invoice' || 
        order.payment_status === 'paid' ||
        order.order_status // Has an order status means it's been processed
      );
      setOrders(paidOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getOrderStatus = (order) => {
    return order.order_status || order.fulfillment_status || 'pending';
  };

  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(getOrderStatus(order));
    setTrackingNumber(order.tracking_number || '');
    setCarrier(order.shipping_carrier || '');
    setShowStatusModal(true);
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder || !newStatus) return;
    
    setUpdatingId(selectedOrder.id);
    try {
      const updateData = {
        order_status: newStatus,
        status: selectedOrder.status // Keep the original internal status
      };
      
      // Add tracking info if provided and status is shipped
      if (newStatus === 'shipped' && trackingNumber) {
        updateData.tracking_number = trackingNumber;
        updateData.shipping_carrier = carrier || 'USPS';
      }
      
      await axios.put(
        `${API_URL}/api/admin/sales-orders/${selectedOrder.id}/update`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Order status updated to ${newStatus}`);
      setShowStatusModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Failed to update order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdatingId(null);
    }
  };

  // Bulk selection handlers
  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAllFiltered = () => {
    if (selectedOrderIds.length === filteredOrders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(filteredOrders.map(o => o.id));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedOrderIds.length === 0 || !bulkStatus) return;
    
    setBulkUpdating(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const orderId of selectedOrderIds) {
      try {
        await axios.put(
          `${API_URL}/api/admin/sales-orders/${orderId}/update`,
          { order_status: bulkStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        successCount++;
      } catch (error) {
        console.error(`Failed to update order ${orderId}:`, error);
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Updated ${successCount} order(s) to ${bulkStatus}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to update ${failCount} order(s)`);
    }
    
    setShowBulkModal(false);
    setBulkStatus('');
    setSelectedOrderIds([]);
    setBulkUpdating(false);
    fetchOrders();
  };

  const getStatusConfig = (status) => {
    return STATUS_FLOW.find(s => s.value === status) || STATUS_FLOW[0];
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.so_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const orderStatus = getOrderStatus(order);
    const matchesStatus = statusFilter === 'all' || orderStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-status-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-neutral-800">Order Fulfillment</h2>
          <p className="text-sm text-neutral-500">Track and update order status</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOrders} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Status Legend */}
      <div className="flex flex-wrap gap-2 p-4 bg-neutral-50 rounded-lg border">
        <span className="text-sm font-medium text-neutral-600 mr-2">Status Flow:</span>
        {STATUS_FLOW.slice(0, 4).map((status, idx) => (
          <React.Fragment key={status.value}>
            <Badge className={`${status.color} border`}>
              <status.icon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
            {idx < 3 && <ArrowRight className="w-4 h-4 text-neutral-400" />}
          </React.Fragment>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by order number, customer name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_FLOW.map(status => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Selection Bar */}
      {filteredOrders.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-neutral-100 rounded-lg">
          <div className="flex items-center gap-3">
            <button
              onClick={selectAllFiltered}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'bg-white border-neutral-300 hover:border-amber-400'
              }`}
              role="checkbox"
              aria-checked={selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0}
              data-testid="select-all-orders-checkbox"
            >
              {selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0 && (
                <CheckCircle className="w-3 h-3" />
              )}
            </button>
            <span className="text-sm text-neutral-600">
              {selectedOrderIds.length > 0 
                ? `${selectedOrderIds.length} selected`
                : `Select orders for bulk update`
              }
            </span>
          </div>
          {selectedOrderIds.length > 0 && (
            <Button
              size="sm"
              onClick={() => setShowBulkModal(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Bulk Update Status
            </Button>
          )}
        </div>
      )}

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg border">
          <Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">No orders found</p>
          <p className="text-sm text-neutral-400 mt-1">
            {statusFilter !== 'all' ? 'Try changing the status filter' : 'Orders will appear here after checkout'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map(order => {
            const status = getOrderStatus(order);
            const statusConfig = getStatusConfig(status);
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedOrder === order.id;

            return (
              <motion.div
                key={order.id}
                layout
                className="border rounded-lg bg-white overflow-hidden"
              >
                {/* Order Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Selection checkbox */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOrderSelection(order.id);
                      }}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedOrderIds.includes(order.id)
                          ? 'bg-amber-500 border-amber-500 text-white'
                          : 'bg-white border-neutral-300 hover:border-amber-400'
                      }`}
                      role="checkbox"
                      aria-checked={selectedOrderIds.includes(order.id)}
                      data-testid={`order-select-checkbox-${order.id}`}
                    >
                      {selectedOrderIds.includes(order.id) && (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </button>
                    <button className="p-1">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-neutral-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-neutral-400" />
                      )}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-neutral-800">
                          #{order.so_number || order.id?.slice(0, 8)}
                        </span>
                        <Badge className={`${statusConfig.color} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-neutral-500">
                        <span>{order.customer_name}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(order.created_at)}
                        </span>
                        <span className="font-medium text-neutral-700">
                          {formatPrice(order.total_amount || order.subtotal)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openStatusModal(order);
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Update Status
                  </Button>
                </div>

                {/* Order Details (Expanded) */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t bg-neutral-50"
                    >
                      <div className="p-4 grid md:grid-cols-3 gap-4">
                        {/* Customer Info */}
                        <div>
                          <h4 className="font-medium text-neutral-700 mb-2">Customer</h4>
                          <div className="space-y-1 text-sm">
                            <p className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-neutral-400" />
                              {order.customer_email}
                            </p>
                            {order.customer_phone && (
                              <p className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-neutral-400" />
                                {order.customer_phone}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Shipping Address */}
                        {order.shipping_address && (
                          <div>
                            <h4 className="font-medium text-neutral-700 mb-2">Shipping To</h4>
                            <div className="text-sm text-neutral-600">
                              <p className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-neutral-400 mt-0.5" />
                                <span>
                                  {order.shipping_address.street}<br />
                                  {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                                </span>
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Tracking Info */}
                        <div>
                          <h4 className="font-medium text-neutral-700 mb-2">Tracking</h4>
                          {order.tracking_number ? (
                            <div className="text-sm">
                              <p className="text-neutral-600">
                                <span className="font-medium">{order.shipping_carrier || 'USPS'}:</span>{' '}
                                {order.tracking_number}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-neutral-400">No tracking info yet</p>
                          )}
                        </div>
                      </div>

                      {/* Items */}
                      <div className="px-4 pb-4">
                        <h4 className="font-medium text-neutral-700 mb-2">Items ({order.items?.length || 0})</h4>
                        <div className="bg-white rounded-lg border divide-y">
                          {(order.items || order.line_items || []).slice(0, 5).map((item, idx) => (
                            <div key={idx} className="p-3 flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">{item.product_type || item.description || 'Custom Shade'}</p>
                                <p className="text-xs text-neutral-500">
                                  {item.width}"W x {item.height}"H • {item.fabric_material || item.material}
                                </p>
                              </div>
                              <span className="text-sm font-medium">{formatPrice(item.unit_price || item.price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>
              Change the fulfillment status of this order and optionally add tracking information.
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-neutral-50 rounded-lg">
                <p className="font-semibold">Order #{selectedOrder.so_number || selectedOrder.id?.slice(0, 8)}</p>
                <p className="text-sm text-neutral-600">{selectedOrder.customer_name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">New Status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FLOW.map(status => (
                      <SelectItem key={status.value} value={status.value}>
                        <div className="flex items-center gap-2">
                          <status.icon className="w-4 h-4" />
                          {status.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tracking Fields (shown when shipping) */}
              {newStatus === 'shipped' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium mb-2">Carrier</label>
                    <Select value={carrier} onValueChange={setCarrier}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USPS">USPS</SelectItem>
                        <SelectItem value="UPS">UPS</SelectItem>
                        <SelectItem value="FedEx">FedEx</SelectItem>
                        <SelectItem value="DHL">DHL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Tracking Number</label>
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking number"
                    />
                  </div>
                </motion.div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={updateOrderStatus}
              disabled={updatingId === selectedOrder?.id}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {updatingId === selectedOrder?.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Status Update</DialogTitle>
            <DialogDescription>
              Update the status of {selectedOrderIds.length} selected order(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{selectedOrderIds.length}</strong> order(s) will be updated
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">New Status</label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status for all" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FLOW.filter(s => s.value !== 'shipped').map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      <div className="flex items-center gap-2">
                        <status.icon className="w-4 h-4" />
                        {status.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                Note: "Shipped" status requires individual tracking numbers, so it's excluded from bulk update.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpdate}
              disabled={bulkUpdating || !bulkStatus}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {bulkUpdating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Update {selectedOrderIds.length} Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderStatusManager;
